// client/src/modules/registry.ts
import type { ModuleManifest, ModuleId, NavItem } from './types';

// Import all module manifests
import crmModule from './crm';           // P1 - CRM
import quotingModule from './quoting';   // P2 - Quoting
import billingModule from './billing';   // P4 - Billing & Collections
import supportModule from './support';   // P5 - Customer Support
import explorerModule from './explorer'; // P6 - Executive Command Center & Data Hub
import fleetModule from './fleet';       // P7 - Fleet Management
import formsModule from './forms';       // P8 - Field Service Forms
import aiSystemsModule from './ai-systems'; // AI Systems - Internal admin module
import projectsModule from './projects';   // P10 - Project Management
import fieldServiceModule from './fieldService'; // P11 - Field Service
import hrModule from './hr';                     // P9 - HR
import technicianModule from './technician';       // P12 - Technician
import timeModule from './time';                     // P13 - Time Tracking
import recruitingModule from './recruiting';         // P14 - Recruiting
import marketingModule from './marketing';         // P15 - Digital Marketing
import huddleModule from './huddle';                 // P16 - Huddle Hub
import workshopModule from './workshop';           // P19 - Workshop (per-user productivity)
import safetyModule from './safety';               // P17 - Safety
import investorRelationsModule from './investorRelations'; // P18 - Investor Relations
import insuranceModule from './insurance';            // P20 - Insurance
import shopModule from './shop';            // SHOP - Shop

// All modules in phase order (AI Systems added at end)
const modules: ModuleManifest[] = [
  crmModule,       // P1
  quotingModule,   // P2
  billingModule,   // P4
  supportModule,   // P5
  explorerModule,  // P6 - Executive + Data Hub
  fleetModule,     // P7 (fully migrated with routes)
  formsModule,     // P8
  hrModule,        // P9
  projectsModule,  // P10
  fieldServiceModule, // P11
  technicianModule,   // P12
  timeModule,         // P13
  recruitingModule,   // P14
  marketingModule,    // P15
  huddleModule,       // P16 - Huddle Hub
  safetyModule,       // P17
  investorRelationsModule, // P18
  workshopModule,     // P19 - Workshop
  insuranceModule,     // P20
  shopModule,     // SHOP
  aiSystemsModule, // AI Systems
];

// Build type-safe lookup map from modules array
const moduleMap: Record<string, ModuleManifest> = Object.fromEntries(
  modules.map(m => [m.id, m])
);

export const ModuleRegistry = {
  /**
   * Get all registered modules in phase order
   */
  getAll: (): ModuleManifest[] => modules,

  /**
   * Get module by ID (P1, P2, etc.)
   */
  getById: (id: ModuleId): ModuleManifest | undefined => moduleMap[id],

  /**
   * Detect module from URL pathname
   */
  getByUrlPrefix: (pathname: string): ModuleManifest | undefined =>
    modules.find(m => pathname.startsWith(m.urlPrefix)),

  /**
   * Get all route configs for React Router
   */
  getAllRoutes: () => modules.map(m => ({
    path: m.urlPrefix.slice(1), // Remove leading slash for nested routes
    children: m.routes,
  })),

  /**
   * Get nav items for a specific module
   */
  getNavItems: (id: ModuleId): NavItem[] => moduleMap[id]?.navItems ?? [],

  /**
   * Get all collection constants merged (filters out modules without collections)
   */
  getAllCollections: (): Record<string, string> =>
    modules.reduce((acc, m) => ({ ...acc, ...(m.collections ?? {}) }), {}),
};

// Re-export types for convenience
export type { ModuleManifest, ModuleId, NavItem } from './types';
